/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useCallback, useEffect, useRef } from "react";
import { Title, Text, Button } from "rizzui";
import { Loader } from "rizzui";
import NotificationItem from "./NotificationItem";
import SimpleBar from "@/components/ui/simplebar";
import { NavigateFunction, useNavigate } from "react-router-dom";
import NotificationRepository from "@/utilities/repositories/Notification";
import { IAdminNotification } from "@/utilities/types/Notification";
import {
  NotificationItem as NotificationItemType,
  useNotification,
} from "./NotificationContext";
import { routes } from "@/config/routes";
import { useAtom } from "jotai";
import notiPaginationDerivedAtom from "@/store/atoms/notification_atom";
import useProfile from "@/hooks/use-profile";
import { Roles } from "@/utilities/types/Users";
import { IoMdClose } from "react-icons/io";

interface NotificationListProps {
  onClose?: () => void;
}

const mapAdminNotifications = (
  items: IAdminNotification[],
): NotificationItemType[] =>
  items.map((item: IAdminNotification) => ({
    id: item.id,
    title: item.title,
    message: item.description,
    timestamp: item.createdAt,
    isRead: item.isRead,
    type: item.type,
    userId: item.data?.userId,
    data: item.data,
  }));

const NotificationList: React.FC<NotificationListProps> = ({ onClose }) => {
  const {
    notifications,
    markAllAsRead,
    clearAllNotifications,
    pushDefaultNotifications,
    unreadCountApi,
    updateUnreadCountApi,
    removeNotification,
  } = useNotification();
  const [loading, setLoading] = React.useState(false);
  const [pagination, setPagination] = useAtom(notiPaginationDerivedAtom);
  const observer = useRef<IntersectionObserver>();
  const loadingRef = useRef(false);
  const paginationRef = useRef(pagination);
  const navigate = useNavigate();
  const { profile } = useProfile();
  const roleRef = useRef(profile?.role);

  useEffect(() => {
    paginationRef.current = pagination;
  }, [pagination]);

  useEffect(() => {
    roleRef.current = profile?.role;
  }, [profile?.role]);

  useEffect(() => {
    return () => {
      observer.current?.disconnect();
      observer.current = undefined;
    };
  }, []);

  const fetchNotifications = useCallback(async () => {
    const currentPagination = paginationRef.current;

    if (
      currentPagination.currentPage === currentPagination.totalPage ||
      roleRef.current !== Roles.ADMIN
    ) {
      return;
    }

    const nextPage = currentPagination.currentPage + 1;
    const res = await NotificationRepository.getAdminNotifications({
      queryParams: {
        pagination: {
          page: nextPage,
          limit: currentPagination.limit,
        },
      },
    });

    if (!res.data) return;

    const nextNotifications = mapAdminNotifications(res.data.items || []);
    pushDefaultNotifications(nextNotifications);
    setPagination({
      total: res.data.total || 0,
      limit: res.data.limit || currentPagination.limit || 10,
      currentPage: res.data.currentPage || nextPage,
      totalPage: res.data.totalPage || 0,
    });
  }, [pushDefaultNotifications, setPagination]);

  const refreshLoadedNotifications = useCallback(
    async (deletedIndex: number) => {
      if (roleRef.current !== Roles.ADMIN) return;

      const currentPagination = paginationRef.current;
      const pageSize = currentPagination.limit || 10;
      const deletedPage = Math.floor(deletedIndex / pageSize) + 1;
      const deletedOffset = deletedIndex % pageSize;
      const preservedNotifications = notifications.slice(0, deletedIndex);

      let mergedNotifications = preservedNotifications;
      let latestPagination = currentPagination;
      let latestUnreadCount = unreadCountApi;

      for (
        let page = deletedPage;
        page <= currentPagination.currentPage;
        page += 1
      ) {
        const res = await NotificationRepository.getAdminNotifications({
          queryParams: {
            pagination: {
              page,
              limit: pageSize,
            },
          },
        });

        if (!res.data) break;

        const pageNotifications = mapAdminNotifications(res.data.items || []);
        const visibleNotifications =
          page === deletedPage
            ? pageNotifications.slice(deletedOffset)
            : pageNotifications;

        mergedNotifications = [...mergedNotifications, ...visibleNotifications];
        latestUnreadCount = res.data.unreadCount ?? latestUnreadCount;
        latestPagination = {
          total: res.data.total || latestPagination.total,
          limit: res.data.limit || pageSize,
          currentPage: res.data.currentPage || page,
          totalPage: res.data.totalPage || latestPagination.totalPage,
          isLoadMore:
            (res.data.currentPage || page) <=
            (res.data.totalPage || latestPagination.totalPage),
        };
      }

      clearAllNotifications();
      pushDefaultNotifications(mergedNotifications);
      updateUnreadCountApi(latestUnreadCount);
      setPagination(latestPagination);
    },
    [
      clearAllNotifications,
      notifications,
      pushDefaultNotifications,
      unreadCountApi,
      updateUnreadCountApi,
      setPagination,
    ],
  );

  // Note: Load more when the last notification becomes visible.
  const lastNotificationRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (loadingRef.current) return;
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver(async (entries) => {
        if (entries[0].isIntersecting) {
          if (loadingRef.current) return;
          loadingRef.current = true;
          setLoading(true);
          try {
            await fetchNotifications();
          } catch (error) {
            console.error("Load more error:", error);
          } finally {
            loadingRef.current = false;
            setLoading(false);
          }
        }
      });

      if (node) observer.current.observe(node);
    },
    [fetchNotifications],
  );

  const readAllNotifications = async () => {
    try {
      await NotificationRepository.readAllNotifications();
    } catch (e) {
      console.error(e);
    }
  };

  const handleMarkAllAsRead = () => {
    markAllAsRead();
    if (unreadCountApi > 0) {
      updateUnreadCountApi(0);
      readAllNotifications();
    }
  };

  const handleClearAll = () => {
    clearAllNotifications();
  };

  const handleDelete = async (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    id: string,
  ) => {
    e.stopPropagation();
    if (id) {
      const shouldLockLoading = roleRef.current === Roles.ADMIN;

      if (shouldLockLoading) {
        loadingRef.current = true;
        setLoading(true);
      }

      try {
        await NotificationRepository.deleteNotification(id);

        const deletedIndex = notifications.findIndex(
          (notification) => notification.id === id,
        );

        if (deletedIndex >= 0 && roleRef.current === Roles.ADMIN) {
          removeNotification(id);
          try {
            await refreshLoadedNotifications(deletedIndex);
          } catch (refreshError) {
            console.error(
              "Failed to refresh notifications after delete:",
              refreshError,
            );
          }
        } else {
          removeNotification(id);
        }
      } catch (error) {
        console.error("Failed to delete notification:", error);
      } finally {
        if (shouldLockLoading) {
          loadingRef.current = false;
          setLoading(false);
        }
      }
    }
  };

  return (
    <div className="w-[280px] text-left sm:w-[360px] 2xl:w-[420px]">
      <div className="flex flex-row items-center justify-between gap-1 p-3">
        <Title
          as="h5"
          fontWeight="semibold"
          className="whitespace-nowrap"
        >
          Notifications
          {profile?.role === Roles.ADMIN && ` (${notifications.length})`}
        </Title>
        <Button
          variant="text"
          size="sm"
          className="shrink-0 text-center leading-5 hover:underline"
          onClick={handleMarkAllAsRead}
        >
          Mark all as read
        </Button>
      </div>

      <SimpleBar className="max-h-[420px] w-full">
        {notifications.length > 0 ? (
          <div className="grid grid-cols-1 gap-2 p-3 w-full">
            {notifications.map((notification, index) => (
              <div
                key={notification.id}
                ref={
                  index === notifications.length - 1
                    ? lastNotificationRef
                    : null
                }
              >
                <NotificationItem
                  notification={notification}
                  onClose={onClose}
                  handleDelete={handleDelete}
                />
              </div>
            ))}
            {loading && (
              <div className="flex justify-center p-2">
                <Loader />
              </div>
            )}
          </div>
        ) : (
          <div className="flex h-32 items-center justify-center p-3 w-full">
            <Text className="text-center text-gray-500 dark:text-gray-400">
              No notifications yet
            </Text>
          </div>
        )}
      </SimpleBar>
    </div>
  );
};

export default NotificationList;
